import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  DEMO_PRESETS,
  ORG_TYPE_LABELS,
  RENEWAL_PRESETS,
  fetchEntitlementEvents,
  formatDate,
  updateEntitlement,
  type EntitlementAction,
  type EntitlementEvent,
  type EntitlementResult,
  type OrgEntitlement,
  type OrgType,
} from "@/lib/entitlements";

const INDEPENDENT_CHOICE = "__independent__";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Context that holds the entitlement. Empty when granting to a user with none. */
  organisationId: string;
  organisationName: string;
  enabled: boolean;
  expiresAt: string | null;
  source?: "standard" | "demo" | null;
  /** Kind of context — independent contexts affect one person only. */
  orgType?: OrgType | null;
  /** Optional user context when opened from Users & Roles. */
  userLabel?: string;
  targetUserId?: string;
  memberCount?: number;
  /** Which action the admin picked in the menu. */
  initialMode?: "renew" | "demo_grant" | "grant_access" | "revoke";
  /** Contexts selectable when granting access to a user without one. */
  organisations?: OrgEntitlement[];
  onUpdated?: (result: EntitlementResult) => void;
}

export function RenewAccessDialog({
  open,
  onOpenChange,
  organisationId,
  organisationName,
  enabled,
  expiresAt,
  source,
  orgType,
  userLabel,
  targetUserId,
  memberCount,
  initialMode = "renew",
  organisations,
  onUpdated,
}: Props) {
  const { toast } = useToast();
  const granting = initialMode === "grant_access";
  const revoking = initialMode === "revoke";
  const independentContext = orgType === "independent";
  const [mode, setMode] = useState<"renew" | "demo_grant">(
    initialMode === "demo_grant" ? "demo_grant" : "renew",
  );
  const [days, setDays] = useState<number | null>(90);
  const [customDate, setCustomDate] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [orgChoice, setOrgChoice] = useState<string>(
    organisationId || (targetUserId ? INDEPENDENT_CHOICE : ""),
  );
  const [events, setEvents] = useState<EntitlementEvent[]>([]);

  const expired = !!expiresAt && new Date(expiresAt).getTime() <= Date.now();

  useEffect(() => {
    if (!open) return;
    setReason("");
    setCustomDate("");
    setConfirming(false);
    setOrgChoice(organisationId || (targetUserId ? INDEPENDENT_CHOICE : ""));

    setMode(initialMode === "demo_grant" ? "demo_grant" : "renew");
    setDays(initialMode === "demo_grant" ? 14 : 90);
    if (organisationId) {
      fetchEntitlementEvents(organisationId)
        .then(setEvents)
        .catch(() => setEvents([]));
    } else {
      setEvents([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, organisationId, initialMode]);

  useEffect(() => {
    if (!open) return;
    setDays(mode === "demo_grant" ? 14 : 90);
    setCustomDate("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const submit = async (action: EntitlementAction) => {
    if (reason.trim().length < 3) {
      toast({
        title: "Reason required",
        description: "Enter a short reason — it is recorded in the audit trail.",
        variant: "destructive",
      });
      return;
    }
    if ((action === "grant_access" || action === "assign_organisation") && !orgChoice) {
      toast({
        title: "Context required",
        description: "Choose the organisation this user should belong to.",
        variant: "destructive",
      });
      return;
    }
    if (!confirming) {
      setConfirming(true);
      return;
    }
    setSaving(true);
    try {
      const trimmedReason = reason.trim();
      const period =
        action === "revoke"
          ? {}
          : customDate
            ? { expires_at: new Date(`${customDate}T23:59:59`).toISOString() }
            : { days: days ?? 90 };

      // Demo access for a user without a context: create the context first.
      if (granting && action === "demo_grant" && !organisationId && targetUserId) {
        await updateEntitlement({
          user_id: targetUserId,
          ...(orgChoice && orgChoice !== INDEPENDENT_CHOICE
            ? { action: "grant_access" as const, organisation_id: orgChoice }
            : { action: "ensure_independent_context" as const }),
          days: 1,
          reason: trimmedReason,
        });
      }

      const result = await updateEntitlement({
        ...(targetUserId ? { user_id: targetUserId } : {}),
        ...(action === "grant_access"
          ? { organisation_id: orgChoice }
          : organisationId
            ? { organisation_id: organisationId }
            : {}),
        action,
        ...period,
        reason: trimmedReason,
      });
      toast({
        title:
          action === "revoke"
            ? "Access revoked"
            : action === "demo_grant"
              ? "Demo access granted"
              : action === "ensure_independent_context"
                ? "Independent access granted"
                : action === "grant_access"
                  ? "Access granted"
                  : "Access renewed",
        description:
          action === "revoke"
            ? `${userLabel || organisationName} can no longer run assessments.`
            : `${userLabel || organisationName} now has access until ${formatDate(result.expires_at)}.`,
      });
      onUpdated?.(result);
      onOpenChange(false);
    } catch (e) {
      toast({
        title: "Update failed",
        description: (e as Error).message,
        variant: "destructive",
      });
      setConfirming(false);
    } finally {
      setSaving(false);
    }
  };

  const presets = mode === "demo_grant" ? DEMO_PRESETS : RENEWAL_PRESETS;
  const primaryAction: EntitlementAction = revoking
    ? "revoke"
    : granting
      ? mode === "demo_grant"
        ? "demo_grant"
        : orgChoice === INDEPENDENT_CHOICE
          ? "ensure_independent_context"
          : "grant_access"
      : mode;


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {revoking ? "Revoke Age-Friendly Index access" : "Age-Friendly Index access"}
          </DialogTitle>
          <DialogDescription>
            {userLabel ? `${userLabel}${organisationName ? ` · ${organisationName}` : ""}` : organisationName}
          </DialogDescription>
        </DialogHeader>

        {/* Current state */}
        <div className="border border-border bg-muted/30 p-3 text-sm space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Status:</span>
            {!organisationId ? (
              <Badge variant="outline">Not granted</Badge>
            ) : !enabled ? (
              <Badge variant="outline">Revoked</Badge>
            ) : expired ? (
              <Badge variant="destructive">Expired</Badge>
            ) : (
              <Badge variant="secondary">Active</Badge>
            )}
            {source === "demo" && <Badge variant="outline">Demo / Test access</Badge>}
            {orgType && (
              <Badge variant="outline">{ORG_TYPE_LABELS[orgType]}</Badge>
            )}
          </div>
          <p className="text-muted-foreground">
            {organisationId
              ? expiresAt
                ? `${expired ? "Expired on" : "Expires on"} ${formatDate(expiresAt)}`
                : "No expiry set"
              : "This user has no assessment context yet."}
          </p>
          {!granting && organisationId && (
            <p className="text-xs text-foreground">
              {independentContext
                ? "This is an independent context — the change affects this user only."
                : `Entitlement is organisation-wide — this affects all ${memberCount ?? 1} member${
                    (memberCount ?? 1) === 1 ? "" : "s"
                  } of ${organisationName}.`}
            </p>
          )}
        </div>

        {granting && (
          <div>
            <Label className="text-xs">Assessment context</Label>
            <Select value={orgChoice} onValueChange={setOrgChoice}>
              <SelectTrigger className="mt-1.5 h-9">
                <SelectValue placeholder="Select a context" />
              </SelectTrigger>
              <SelectContent>
                {targetUserId && (
                  <SelectItem value={INDEPENDENT_CHOICE}>
                    Independent — this user only
                  </SelectItem>
                )}
                {(organisations || [])
                  .filter((o) => o.orgType !== "independent")
                  .map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.name} · {ORG_TYPE_LABELS[o.orgType]} · {o.memberCount} member
                      {o.memberCount === 1 ? "" : "s"}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <p className="mt-1.5 text-xs text-muted-foreground">
              {orgChoice === INDEPENDENT_CHOICE
                ? "Creates (or reuses) this person's own independent context. Nobody else is affected."
                : "Adds this user to the organisation — the entitlement applies to all its members."}
            </p>
          </div>
        )}


        {!revoking && (
          <>
            <Tabs value={mode} onValueChange={(v) => setMode(v as typeof mode)}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="renew">
                  {granting ? "Grant access" : "Renew access"}
                </TabsTrigger>
                <TabsTrigger value="demo_grant">Demo / Test access</TabsTrigger>
              </TabsList>
              <TabsContent value="demo_grant" className="pt-2">
                <p className="text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">DEMO / TEST ACCESS</span> —
                  short-lived, clearly labelled test access. It uses the same server-side
                  expiry checks, never becomes production entitlement, and does not affect
                  scoring, LIP or AI behaviour.
                </p>
              </TabsContent>
              <TabsContent value="renew" className="pt-2">
                <p className="text-xs text-muted-foreground">
                  Standard licensed access. The new expiry applies immediately{" "}
                  {independentContext || orgChoice === INDEPENDENT_CHOICE
                    ? "to this independent user."
                    : "to every member of the organisation."}
                </p>
              </TabsContent>

            </Tabs>

            <div className="space-y-3">
              <div>
                <Label className="text-xs">Period</Label>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {presets.map((p) => (
                    <Button
                      key={p.days}
                      type="button"
                      size="sm"
                      variant={!customDate && days === p.days ? "default" : "outline"}
                      className="h-8 text-xs"
                      onClick={() => {
                        setDays(p.days);
                        setCustomDate("");
                        setConfirming(false);
                      }}
                    >
                      {p.label}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="custom-expiry" className="text-xs">
                  Or choose an exact expiry date
                </Label>
                <Input
                  id="custom-expiry"
                  type="date"
                  value={customDate}
                  min={new Date(Date.now() + 86_400_000).toISOString().slice(0, 10)}
                  onChange={(e) => {
                    setCustomDate(e.target.value);
                    setConfirming(false);
                  }}
                  className="mt-1.5 h-9"
                />
              </div>
            </div>
          </>
        )}

        <div>
          <Label htmlFor="reason" className="text-xs">
            Reason (recorded in the audit trail)
          </Label>
          <Textarea
            id="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="e.g. Pilot extension approved by programme lead"
            className="mt-1.5 text-sm"
          />
        </div>

        {events.length > 0 && !revoking && (
          <div className="border-t border-border pt-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Recent changes
            </p>
            <ul className="mt-2 space-y-1.5 max-h-32 overflow-y-auto">
              {events.map((ev) => (
                <li key={ev.id} className="text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">
                    {ev.action.replace("_", " ")}
                  </span>{" "}
                  · {formatDate(ev.previous_expires_at)} → {formatDate(ev.new_expires_at)} ·{" "}
                  {ev.actor_email || "unknown"} ·{" "}
                  {new Date(ev.created_at).toLocaleString("en-GB")}
                  {ev.reason ? ` · ${ev.reason}` : ""}
                </li>
              ))}
            </ul>
          </div>
        )}

        {confirming && (
          <p className="text-xs text-destructive">
            Confirm to apply this change. It takes effect immediately and is audited.
          </p>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          {!revoking && organisationId && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={saving || !enabled}
              onClick={() => submit("revoke")}
            >
              Revoke now
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            variant={revoking ? "destructive" : "default"}
            disabled={saving}
            onClick={() => submit(primaryAction)}
          >
            {saving
              ? "Saving…"
              : confirming
                ? "Confirm"
                : revoking
                  ? "Revoke access"
                  : primaryAction === "demo_grant"
                    ? "Grant demo access"
                    : granting
                      ? "Grant access"
                      : "Renew access"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RenewAccessDialog;
